import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Storage } from '@ionic/storage';
import { Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ReportsService } from 'src/app/metrics/services/reports.service';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { TimeIntervalEnum } from '../../shared/enums';
import { DashboardCard, DashboardCardRaw, DashboardFilter } from '../../shared/models';

@Component({
  selector: 'app-dashboard-overview',
  templateUrl: './dashboard-overview.component.html',
  styleUrls: ['./dashboard-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardOverviewComponent implements OnInit, OnDestroy {
  @Input() routingTagItems: Choice[];
  @Input() routingTagLabel: string;
  REFRESH_DELAY_MS = 60000;
  timer: any;
  predefinedIntervals: Choice[];
  dashboardTimeIntervalLocalStorageKey = `dashboard-time-interval-${this.alcmeonContextService.companyId}`;
  routingTagsLocalStorageKey = `dashboard-routing-tags-${this.alcmeonContextService.companyId}`;
  isLoading = false;
  cards: DashboardCard[] = null;

  form: FormGroup = this.formBuilder.group({
    timeIntervalType: [TimeIntervalEnum.today],
    routingTags: [[]],
  });

  private subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private storage: Storage,
    private formBuilder: FormBuilder,
    private reportsService: ReportsService,
    private utilsService: UtilsService
  ) {}

  async ngOnInit() {
    this.setPredefinedIntervals();

    this.subscriptions.add(
      this.form.valueChanges.pipe(debounceTime(300)).subscribe(value => {
        this.updateDashboardData(value, true);
      })
    );

    // When there is no data in localStorage the form.valueChanges is not triggered,
    // also valueChanges have to be before the await call -> if not it doesn't work
    const timeIntervalType = await this.storage.get(this.dashboardTimeIntervalLocalStorageKey);
    const routingTags = await this.storage.get(this.routingTagsLocalStorageKey);

    if (timeIntervalType === null && routingTags === null) {
      this.updateDashboardData(this.form.value, true);
    }
  }

  private updateDashboardData(dashboardFilters: DashboardFilter, showSpinner: boolean) {
    if (showSpinner) {
      this.cards = null;
    }
    this.reportsService
      .postForDashboard(dashboardFilters)
      .pipe(map(cards => cards.map(this.formatCardModel)))
      .subscribe(data => {
        this.cards = data;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.updateDashboardData(dashboardFilters, false), this.REFRESH_DELAY_MS);
      });
  }

  private setPredefinedIntervals() {
    this.predefinedIntervals = [
      {
        label: this.basicService.translate('metric-reports/list/interval_filter/today'),
        id: TimeIntervalEnum.today,
      },
      {
        label: this.basicService.translate('metric-reports/list/interval_filter/seven_days'),
        id: TimeIntervalEnum.lastSevenDays,
      },
      {
        label: this.basicService.translate('metric-reports/list/interval_filter/thirty_days'),
        id: TimeIntervalEnum.lastThirtyDays,
      },
    ];
  }

  private formatCardModel = (card: DashboardCardRaw): DashboardCard => {
    const metricId = card['metric-id'];
    const title = card.label;
    const color = `color-${card['color-index']}`;
    const primaryLabel = card.title.label;
    const primaryValue = this.formatTypedValue(card.title.type, card.title.value);
    const secondaryLabel = card.subtitle.label;
    const secondaryValue = this.formatTypedValue(card.subtitle.type, card.subtitle.value);

    return {
      title: title,
      color: color,
      primaryMetric: {
        label: primaryLabel,
        value: primaryValue,
      },
      secondaryMetric: {
        label: secondaryLabel,
        value: secondaryValue,
      },
      metricId: metricId,
    };
  };

  /**
   * @param value: string or number send by backend
   */
  private formatTypedValue(type: string, value: any): number | string {
    let formatedValue = value;
    if (type === 'timestamp_delta') {
      formatedValue = this.utilsService.formatDuration(value, true);
    } else if (type === 'percent') {
      formatedValue = `${value}%`;
    }
    return formatedValue;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
    clearTimeout(this.timer);
  }
}
